import React, { useState } from 'react';

const CUR = (cur) => (cur === 'USD' ? 'US$' : 'A$');
const money = (n, cur) => CUR(cur) + Math.round(n || 0).toLocaleString();
const moneyShort = (n, cur) => {
  const v = Math.abs(n || 0);
  const s = CUR(cur);
  if (v >= 1e6) return s + (n / 1e6).toFixed(v >= 1e7 ? 0 : 1) + 'M';
  if (v >= 1e3) return s + Math.round(n / 1e3) + 'k';
  return s + Math.round(n || 0);
};
const qty = (n) => Math.round(n || 0).toLocaleString();

// 1 / 2 / 5 x 10^n ceiling so the axis ticks read cleanly.
function niceMax(v) {
  if (!v || v <= 0) return 1;
  const exp = Math.floor(Math.log10(v));
  const base = Math.pow(10, exp);
  const step = [1, 2, 2.5, 5, 10].find(s => v <= s * base) || 10;
  return step * base;
}

const TICKS = [1, 0.75, 0.5, 0.25, 0];

export default function SiBarChart({
  buckets, cur, title, testid, primary = 'count', unit = 'quote',
  tone = 'navy', xLabel, footNote,
}) {
  const [hover, setHover] = useState(null);
  const rows = (buckets || []).filter(Boolean);
  if (rows.length === 0) return null;

  const isMoneyBars = primary === 'amount';
  const barOf = b => (isMoneyBars ? b.amount : b.count) || 0;
  const lineOf = b => (isMoneyBars ? b.count : b.amount);
  const barMax = niceMax(Math.max(...rows.map(barOf), 0));
  const lineVals = rows.map(lineOf).filter(v => v != null);
  const lineMax = lineVals.length ? niceMax(Math.max(...lineVals, 0)) : null;
  const barTotal = rows.reduce((a, b) => a + barOf(b), 0);
  const lineTotal = rows.reduce((a, b) => a + (lineOf(b) || 0), 0);
  const actual = rows.filter(b => !b.fc), projected = rows.filter(b => b.fc);
  const fcStart = rows.findIndex(b => b.fc);
  const sum = (arr, f) => arr.reduce((a, b) => a + (f(b) || 0), 0);

  const leftFmt = v => (isMoneyBars ? moneyShort(v, cur) : qty(v));
  const rightFmt = v => (isMoneyBars ? qty(v) : moneyShort(v, cur));
  const pts = lineMax
    ? rows.map((b, i) => {
      const v = lineOf(b);
      if (v == null) return null;
      return { x: ((i + 0.5) / rows.length) * 100, y: 100 - (v / lineMax) * 100 };
    })
    : [];

  const h = hover != null ? rows[hover] : null;
  const prev = hover != null && hover > 0 ? rows[hover - 1] : null;
  const delta = h && prev && barOf(prev) ? ((barOf(h) - barOf(prev)) / barOf(prev)) * 100 : null;
  const actualBarTotal = sum(actual, barOf);
  const share = h && !h.fc && actualBarTotal ? (barOf(h) / actualBarTotal) * 100 : null;
  const perDoc = h && h.count ? (h.amount || 0) / h.count : null;

  return (
    <div className="si-chart2" data-testid={testid}>
      {title && <div className="si-chart-title">{title}</div>}
      <div className="si-chart2-body">
        <div className="si-axis si-axis-l" aria-hidden="true">
          {TICKS.map(t => <span key={t}>{leftFmt(barMax * t)}</span>)}
        </div>
        <div className="si-plot" onMouseLeave={() => setHover(null)}>
          {TICKS.map(t => <div key={t} className={'si-gline' + (t === 0 ? ' base' : '')} style={{ bottom: t * 100 + '%' }} />)}
          {fcStart > 0 && (
            <div className="si-fc-divider" style={{ left: (fcStart / rows.length) * 100 + '%' }} data-testid={testid ? testid + '-divider' : undefined}>
              <span>forecast →</span>
            </div>
          )}
          {lineMax > 0 && (
            <svg className="si-line" viewBox="0 0 100 100" preserveAspectRatio="none">
              <polyline points={pts.slice(0, fcStart > 0 ? fcStart : pts.length).filter(Boolean).map(p => `${p.x},${p.y}`).join(' ')} vectorEffect="non-scaling-stroke" />
              {fcStart > 0 && <polyline className="proj" points={pts.slice(fcStart - 1).filter(Boolean).map(p => `${p.x},${p.y}`).join(' ')} vectorEffect="non-scaling-stroke" />}
            </svg>
          )}
          {pts.map((p, i) => p && (
            <span key={'d' + i} className={'si-dot' + (hover === i ? ' on' : '')}
              style={{ left: p.x + '%', bottom: (100 - p.y) + '%' }} />
          ))}
          <div className="si-bars2">
            {rows.map((b, i) => (
              <div key={b.key || i} className={'si-bar-slot' + (hover === i ? ' on' : '')}
                data-testid={testid ? testid + '-bar-' + i : undefined}
                onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} tabIndex={-1}>
                <div className={'si-bar2 ' + tone + (b.fc ? ' fc' : '')}
                  style={{ height: Math.max(2, (barOf(b) / barMax) * 100) + '%' }} />
              </div>
            ))}
          </div>
          {h && (
            <div className={'si-tip' + (hover < rows.length / 4 ? ' left' : hover > (rows.length * 3) / 4 ? ' right' : '')}
              style={{ left: ((hover + 0.5) / rows.length) * 100 + '%' }} data-testid={testid ? testid + '-tip' : 'si-chart-tip'}>
              <strong>{h.key}{h.fc ? ' · projected' : ''}</strong>
              <span><i>{unit === 'order' ? 'Orders' : unit === 'quote' ? 'Quotes' : 'Count'}</i>{qty(h.count)}</span>
              <span><i>{h.fc ? 'Projected value' : 'Value'}</i>{h.amount == null ? '—' : money(h.amount, cur)}</span>
              {perDoc != null && <span><i>Avg per {unit}</i>{money(perDoc, cur)}</span>}
              {share != null && <span><i>Share of period</i>{share.toFixed(1)}%</span>}
              {delta != null && <span><i>vs previous</i><b className={delta >= 0 ? 'up' : 'down'}>{delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%</b></span>}
            </div>
          )}
        </div>
        {lineMax > 0 && (
          <div className="si-axis si-axis-r" aria-hidden="true">
            {TICKS.map(t => <span key={t}>{rightFmt(lineMax * t)}</span>)}
          </div>
        )}
      </div>
      <div className="si-chart2-foot">
        <span className="si-legend">
          <i className={'sw ' + tone} />{isMoneyBars ? 'Value (left)' : 'Count (left)'}
          {lineMax > 0 && <><i className="sw line" />{isMoneyBars ? 'Count (right)' : 'Value (right)'}</>}
        </span>
        <span className="si-xrange">{xLabel || (rows[0].key + ' → ' + rows[rows.length - 1].key)}</span>
        <span className="si-chart-tot">
          {projected.length > 0
            ? <>{qty(sum(actual, b => b.count))} {unit}s · {money(sum(actual, b => b.amount), cur)} actual <em>→ {qty(sum(projected, b => b.count))} · {money(sum(projected, b => b.amount), cur)} projected</em></>
            : <>{qty(rows.reduce((a, b) => a + (b.count || 0), 0))} {unit}s · {money(isMoneyBars ? barTotal : lineTotal, cur)}</>}
        </span>
      </div>
      {footNote && <div className="si-chart-note">{footNote}</div>}
    </div>
  );
}
